import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef } from "react";

import {
  addProposalSummary,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import PageTitle from "../../../components/titles/PageTitle";
import InputField from "../../../components/form/InputField";
import ProjectTitle from "../../../components/text/ProjectTitle";
import TextContainer from "../../../components/text/TextContainer";
import MainContainer from "../../../components/container/MainContainer";
import TextArrayContainer from "../../../components/text/TextArrayContainer";
import BudgetTableContainer from "../../../components/text/BudgetTableContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import CategoryBudgetTableContainer from "../../../components/text/CategoryBudgetTableContainer";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProposalSummary = () => {
  const { id } = useParams();
  const pdfRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalSummary
  );
  const addNotification = useNotification();
  const { register, reset, handleSubmit } = useForm();

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProposalSummary(data));
      if (addProposalSummary.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposal/success/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "warning");
    }
  };

  // const downloadPDF = () => {};

  useLayoutEffect(() => {
    dispatch(getProject(id));

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        id: project.id,
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/projects/proposals");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Summary" />
        {project.id && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            encType="multipart/form-data"
          >
            <div
              className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto"
              ref={pdfRef}
            >
              <ProjectTitle title={project.name} />
              <TextContainer
                label="Name of Community"
                content={project.community}
              />
              <TextContainer
                label="Address 1"
                content={project.community_address1}
              />
              <TextContainer
                label="Address 2"
                content={project.community_address2}
              />
              <TextContainer label="Province" content={project.province} />
              <TextContainer label="Country" content={project.country} />
              <TextContainer
                label="Community Contact Name"
                content={`${project.creator?.first_name || "------"} ${
                  project.creator?.last_name || "-----"
                }`}
              />
              <TextContainer
                label="Community Contact Email"
                content={project.creator?.email}
              />
              <TextContainer
                label="Community Contact Number"
                content={project.creator?.phone || "---------"}
              />
              <TextContainer
                label="PDO Contact Name"
                content={`${project.partners[0]?.first_name || "--------"} ${
                  project.partners[0]?.last_name || "--------"
                }`}
              />
              <TextContainer
                label="PDO Contact Email"
                content={project.partners[0]?.email || "--------"}
              />
              <TextContainer
                label="PDO Contact Number"
                content={project.partners[0]?.phone || "--------"}
              />
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <TextContainer
                label="Planned Start Date"
                content={new Date(project.planned_start_date).toDateString()}
              />
              <TextContainer
                label="Planned End Date"
                content={new Date(project.planned_end_date).toDateString()}
              />
              <TextContainer
                label="Currency"
                content={`${
                  project.currency?.name + " (" + project.currency?.symbol + ")"
                }`}
              />
              <TextContainer
                label="Background"
                isHTML
                content={project.description}
              />
              <TextContainer isHTML label="Goal" content={project.aim} />
              <TextContainer isHTML label="Purpose" content={project.purpose} />
              <TextArrayContainer
                label="Activities"
                content={project.activities}
              />
              <TextArrayContainer
                label="Results"
                content={project.expected_results}
              />
              <TextArrayContainer
                label="Objectives"
                content={project.objectives}
              />
              <TextContainer
                isHTML
                label="Selected Indicators"
                content={project.selected_indicators}
              />
              <TextContainer
                isHTML
                label="Sustainability Plan"
                content={project.susstainable_plan}
              />
              <TextContainer
                isHTML
                label="Justification"
                content={project.justification}
              />
              <TextContainer
                isHTML
                label="Monitoring and Supervision"
                content={project.monitoring_supervision}
              />
              <TextContainer
                isHTML
                label="How this project helps address the issues of the GC26 & DC27"
                content={project.issues}
              />
              {/*  */}
              <TextContainer
                label="Target Group"
                content={project.stakeholder_target_group}
              />
              <TextContainer
                label="Male Beneficiaries"
                content={project.male_beneficiaries}
              />
              <TextContainer
                label="Female Beneficiaries"
                content={project.female_beneficiaries}
              />

              {/* Budget */}
              <div className="w-full overflow-x-auto">
                <BudgetTableContainer
                  label="Budget"
                  content={project.budget || []}
                />
              </div>

              {/* Budget by Category*/}
              <div className="w-full overflow-x-auto">
                <CategoryBudgetTableContainer
                  label="Budget by Category"
                  content={project.catBudget || []}
                />
              </div>

              {/* Summary End */}
              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/proposals/budget/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>

                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalSummary;
